import { useEffect, useMemo, useState } from "react";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { DashLayout } from "@/components/DashLayout";
import { TxTable } from "@/components/Tables";
import { WalletFilter } from "@/components/WalletFilter";
import { useAuth } from "@/context/AuthContext";
import { api, fmtDate } from "@/lib/api";
import { USER_NAV } from "@/pages/navs";

const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

const downloadCsv = (rows, user, from, to, wallet) => {
  const head = [["YesPayBizz Account Statement"], ["Merchant", user?.business_name || user?.name], ["Email", user?.email],
    ["Period", `${from || "Start"} to ${to || "Today"}`], ["Wallet", wallet], []];
  const cols = ["Date", "Wallet", "Type", "Description", "UTR", "Amount (INR)", "Balance After (INR)"];
  const body = rows.map((t) => [fmtDate(t.created_at), t.wallet, t.type, t.note, t.utr ? `'${t.utr}` : "", (t.type === "credit" ? "" : "-") + t.amount.toFixed(2), t.balance_after.toFixed(2)]);
  const csv = [...head, cols, ...body].map((r) => r.map(esc).join(",")).join("\r\n");
  const url = URL.createObjectURL(new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url; a.download = `YesPayBizz_Statement_${from || "all"}_${to || "today"}.csv`; a.click();
  URL.revokeObjectURL(url);
};

export default function UserTransactions() {
  const { user, tick } = useAuth();
  const [wallet, setWallet] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [rows, setRows] = useState([]);

  useEffect(() => {
    api.get("/me/transactions", { params: wallet === "all" ? {} : { wallet } }).then((r) => setRows(r.data));
  }, [wallet, tick]);

  const filtered = useMemo(() => rows.filter((t) => {
    const d = new Date(t.created_at);
    if (from && d < new Date(`${from}T00:00:00`)) return false;
    if (to && d > new Date(`${to}T23:59:59`)) return false;
    return true;
  }), [rows, from, to]);

  const download = () => {
    if (!filtered.length) return toast.error("No transactions in selected period");
    downloadCsv(filtered, user, from, to, wallet);
  };

  return (
    <DashLayout nav={USER_NAV} title="Transactions" subtitle="All wallet activity">
      <div className="flex flex-col gap-4 rounded-xl border border-slate-200 bg-white p-4 lg:flex-row lg:items-end">
        <div className="grid flex-1 grid-cols-2 gap-3 sm:max-w-md">
          <label className="text-xs font-medium text-slate-500">From
            <Input data-testid="statement-from-input" type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} className="mt-1" />
          </label>
          <label className="text-xs font-medium text-slate-500">To
            <Input data-testid="statement-to-input" type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} className="mt-1" />
          </label>
        </div>
        <button onClick={download} data-testid="download-statement-btn" className="flex h-10 items-center justify-center gap-2 rounded-md bg-[#2563EB] px-5 text-sm font-semibold text-white transition-colors hover:bg-[#1D4ED8]">
          <Download className="h-4 w-4" /> Download Statement
        </button>
      </div>
      <WalletFilter value={wallet} onChange={setWallet} />
      <TxTable rows={filtered} />
    </DashLayout>
  );
}
